/* import __COLOCATED_TEMPLATE__ from './driver-select.hbs'; */
import Component from '@ember/component';
import { service } from '@ember/service';
import { alias } from '@ember/object/computed';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import Constants from '../utils/constants';
import PrettyNum from '../utils/pretty-num-wrapper';
import Utils from '../utils/utils';

import {
    SlickDataView,
    SlickGrid,
    SlickRowSelectionModel,
    SlickAutoTooltips,
    SlickGroupItemMetadataProvider,
} from 'slickgrid';
import 'slickgrid/dist/styles/css/slick-icons.css';
import 'slickgrid/dist/styles/css/slick-alpine-theme.css';
import $ from 'jquery';

export default class DriverSelect extends Component {
    tagName = '';

    @service('aditools-analytics') analytics;
    @service intl;
    @service urlData;
    @service('data') model;
    @service selectionData;
    @service driverService;
    @service driverSupplyService;
    @service hashDataService;
    @service componentManager;
    @service partData;

    qaAttrValue = 'sel_a_driver';
    gridId = 'driver-select-grid';
    headerColumnRows = 1;

    @alias('selectionData.selectedDriver') selectedItem;

    @alias('selectionData.previousDriver') previousItem;

    @alias('selectionData.selectedAdc') selectedAdc;

    @alias('selectionData.driverInputTypeOptions') driverInputTypeOptions;

    @tracked frozenColumn = 1;
    @tracked pstLinks = [
        {
            href: {
                all: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.OPAMPS}`,
                inThisTool: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.OPAMPS}#/p0=${this.urlData.opAmpFilterString}`,
            },
            name: {
                all: this.intl.t('parametric_search.op_amps.all'),
                inThisTool: this.intl.t('parametric_search.op_amps.in_this_tool'),
            },
        },
        {
            href: {
                all: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.DIFFAMPS}`,
                inThisTool: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.DIFFAMPS}#/p0=${this.urlData.diffAmpFilterString}`,
            },
            name: {
                all: this.intl.t('parametric_search.diff_amps.all'),
                inThisTool: this.intl.t('parametric_search.diff_amps.in_this_tool'),
            },
        },
    ];
    @tracked data = this.model.selectableDriverOptions;
    @tracked options = {
        asyncEditorLoading: true,
        showHeaderRow: true,
        resizable: true,
        forceFitColumns: false,
        multiSelect: false,
        headerRowHeight: 30,
        rowHeight: 25,
        autoHeight: false,
        enableColumnReorder: false,
        multiColumnSort: true,
        numberedMultiColumnSort: true,
        tristateMultiColumnSort: true,
        frozenColumn: this.frozenColumn || 0,
        explicitInitialization: true,
    };
    @tracked columns = [];
    @tracked errorCells = {};
    @tracked selectedPart = '';
    // Configuration for grouping
    @tracked groupItemMetadataProvider = null;
    @tracked groupingConfig = {
        getter(value) {
            return !value.recommended;
        },
        displayTotalsRow: false,
        formatter: (g) => {
            let part = this.selectedAdc.partCore;
            if (g.value == false) {
                // Actually true, but getter inverts it
                let qaAttr = `${Constants.QA_ATTR_NAME}="${Constants.QA_ATTR_PREFIX}_${this.qaAttrValue}_dlg_rec_drivers_lbl"`;
                return `<span ${qaAttr}>${this.intl.t('driver_modal.subheadings.recommended_drivers_for', {
                    part,
                })}</span>`;
            } else {
                let qaAttr = `${Constants.QA_ATTR_NAME}="${Constants.QA_ATTR_PREFIX}_${this.qaAttrValue}_dlg_rec_drivers_on_product_pg_link"`;
                return `<div class='recommended-drivers-link-container'><a href='${this.selectedAdc.datasheet}' class='view-recommended-drivers-link' target='_blank' ${qaAttr}>${this.intl.t(
                    'driver_modal.subheadings.also_view_recommended_drivers',
                    { part },
                )}<span class='link-icon'></span></a></div>`;
            }
        },
    };

    ////////////////////////////////////////////////////////////////////////////////
    // Component Actions                                                          //
    ////////////////////////////////////////////////////////////////////////////////
    @action insertingGrid() {
        const selectedItem = this.selectedItem.part.split('|')[0];
        this.selectedPart = this.data.find((item) => item.part === selectedItem).part;

        this.constructColumns();
        this.createSlickGrid();
        this._resetFilters();
    }

    @action resetFilters() {
        this._resetFilters();

        this.analytics.setWebEvent(this.partData.toolName, 'Reset Filters', `Selected driver: ${this.selectedPart}`);
    }

    @action selectItem(closeDialog) {
        const selectedDriver = this.data.find((item) => item.part === this.selectedPart);
        this.set('previousItem', this.selectionData.selectedDriver);
        this.set('selectedItem', selectedDriver);

        this.model.updateShowVariant('driver');
        this.model.switchVariant('driver');

        this.model.overrideRCValues({ resetRext: true });
        this.model.setDefaultTopology();

        this.driverService.setTopologyStatus();

        this.driverService.defaultRf();
        this.driverService.resetGain();
        this.driverService.resetRf();
        this.driverService.defaultCf();
        this.driverService.resetCf();

        this.driverSupplyService.calculateVSupply();

        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');

        this.hashDataService.setUrlParameter('model.showDriverVariant');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');

        this.hashDataService.setUrlParameter('model.rext');
        this.hashDataService.setUrlParameter('model.cext');

        this.hashDataService.setUrlParameterWithSelectedPart('driver');
        this.hashDataService.setUrlParameterWithSelectedId('topology');

        this.hashDataService.setUrlParameterForObject('showDriverVariants');

        if (this.updateAppData) this.updateAppData();

        this.analytics.setWebEvent(this.partData.toolName, 'Selected Driver', `${this.selectedPart}`);

        closeDialog();
    }

    @action screenResized() {
        if (this.grid) {
            this.grid.resizeCanvas();
        }
    }

    constructColumns() {
        // Formatters
        const starFormatter = (row, cell, value, columnDef, dataContext) => {
            let recommended = dataContext.recommended;
            if (recommended)
                return `<span class="star-formatter" style=" width:100%; display:inline-block; text-align: center; ">★</span>`;
            return '';
        };
        // hyperlink, maybe bold
        const hyperlinkFormatterBold = (row, cell, value, columnDef, dataContext) => {
            let recommended = dataContext.recommended,
                link = dataContext.datasheet,
                // If there is no datasheet url, the datasheet link will not show in the column
                datasheetHtml = link
                    ? `<a target="_blank" href="${link}" class="no-row-select"><span class="no-row-select inline-block link-icon"></span></a>`
                    : '';
            return value
                ? `<span id="${value}RowSelector" class="part-number-cell ${
                      recommended ? 'bold ' : ''
                  }inline-block">${value}${datasheetHtml}</span>`
                : '';
        };
        const prettyNumFormatter = (row, cell, value, columnDef) => {
            return Utils.formatCell(columnDef.dataType, PrettyNum.eng3(value));
        };
        const currencyFormatter = (row, cell, value, columnDef) => {
            let displayValue = value;

            if (!isNaN(value) && value !== null && value !== false && value !== true)
                displayValue = '$' + Number(value).toFixed(2).toString();
            else displayValue = '';

            return Utils.formatCell(columnDef.dataType, displayValue);
        };
        const numericFormatter = (row, cell, value, columnDef) => {
            return Utils.formatCell(columnDef.dataType, value);
        };

        this.columns = [
            {
                id: 'vfoe',
                name: '',
                field: 'recommended',
                width: 30,
                originalWidth: 30,
                formatter: starFormatter,
                dataType: 'boolean',
            },
            {
                name: this.intl.t('column_headers.device'),
                field: 'part',
                id: 'part',
                qaAttrValue: 'dev',
                width: 115,
                originalWidth: 115,
                sortable: true,
                dataType: 'string',
                formatterType: 'hyperlink',
                formatter: hyperlinkFormatterBold,
                toolTip: this.intl.t('column_headers.device'),
            },
            {
                name: this.intl.t('column_headers.driver_type'),
                field: 'driverTypeFormatted',
                id: 'type',
                qaAttrValue: 'typ',
                width: 105,
                sortable: true,
                dataType: 'string',
                options: this.driverInputTypeOptions,
                toolTip: this.intl.t('column_headers.driver_type'),
            },
            {
                id: 'noiseDensity',
                name: `${this.intl.t('column_headers.vnoise_density')} (${this.intl.t('units.v_rthz')})`,
                field: 'vn',
                width: 180,
                originalWidth: 180,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: prettyNumFormatter,
                toolTip: `${this.intl.t('column_headers.vnoise_density')} (${this.intl.t('units.v_rthz')})`,
            },
            {
                id: 'slewRateVus',
                name: `${this.intl.t('column_headers.slew_rate')} (${this.intl.t('units.v_us')})`,
                field: 'slewRateVus',
                width: 135,
                originalWidth: 135,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: prettyNumFormatter,
                toolTip: `${this.intl.t('column_headers.slew_rate')} (${this.intl.t('units.v_us')})`,
            },
            {
                id: 'GBWP',
                name: `${this.intl.t('column_headers.gbw')} (${this.intl.t('units.hz')})`,
                field: 'GBWP',
                width: 110,
                originalWidth: 110,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: prettyNumFormatter,
                toolTip: `${this.intl.t('column_headers.gbw')} (${this.intl.t('units.hz')})`,
            },
            {
                id: 'is',
                name: `${this.intl.t('column_headers.iq')} (${this.intl.t('units.a')})`,
                field: 'is',
                width: 110,
                originalWidth: 110,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: prettyNumFormatter,
                toolTip: `${this.intl.t('column_headers.iq')} (${this.intl.t('units.a')})`,
            },
            {
                id: 'VsMin',
                name: `${this.intl.t('column_headers.vs_span_min')} (${this.intl.t('units.v')})`,
                field: 'VsMin',
                width: 130,
                originalWidth: 130,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: prettyNumFormatter,
                toolTip: `${this.intl.t('column_headers.vs_span_min')} (${this.intl.t('units.v')})`,
            },
            {
                id: 'VsMax',
                name: `${this.intl.t('column_headers.vs_span_max')} (${this.intl.t('units.v')})`,
                field: 'VsMax',
                width: 130,
                originalWidth: 130,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: prettyNumFormatter,
                toolTip: `${this.intl.t('column_headers.vs_span_max')} (${this.intl.t('units.v')})`,
            },
            {
                id: 'Price',
                name: `${this.intl.t('column_headers.price')} (${this.intl.t('units.price')})`,
                field: 'Price',
                width: 130,
                originalWidth: 130,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'pretty-numeric',
                formatter: currencyFormatter,
                toolTip: `${this.intl.t('column_headers.price')} (${this.intl.t('units.price')})`,
            },
            {
                id: 'numAmps',
                name: this.intl.t('column_headers.amps_per_package'),
                field: 'numAmps',
                width: 125,
                originalWidth: 125,
                sortable: true,
                defaultFilterOption: 'greaterthan',
                dataType: 'numeric',
                formatter: numericFormatter,
                toolTip: this.intl.t('column_headers.amps_per_package'),
            },
            {
                id: 'package',
                name: this.intl.t('column_headers.package'),
                field: 'package',
                width: 200,
                originalWidth: 200,
                dataType: 'string',
                sortable: false,
                toolTip: this.intl.t('column_headers.package'),
            },
        ];
    }

    createSlickGrid() {
        // add selected property to data
        this.data = this.data.map((item) => {
            return {
                ...item,
                selected: item.part === this.selectedPart,
            };
        });

        this.columnFilters = {};
        var selfRef = this;

        // Initialize CSS Variable for Header
        const cssRoot = document.querySelector(':root');
        this.headerColumnRows = 1;
        cssRoot.style.setProperty('--alpine-header-name-row-count', this.headerColumnRows.toString());

        this.groupItemMetadataProvider = new SlickGroupItemMetadataProvider();
        this.groupItemMetadataProvider.getGroupRowMetadata = () => {
            let columns = [];
            for (var i = 0; i <= this.frozenColumn; i++) columns[i] = this.columns[i];

            columns.push({
                colspan: '*',
                formatter: (row, cell, value, columnDef, item) => item.title,
                editor: null,
            });

            return {
                selectable: false,
                focusable: false,
                cssClasses: 'slick-group',
                columns: columns,
            };
        };

        if ($('#' + this.gridId).length) {
            $('#' + this.gridId).empty();

            let groupItemMetadataProvider = this.groupItemMetadataProvider;
            this.dataView = new SlickDataView({ inlineFilters: true, groupItemMetadataProvider });
            this.dataView.setItems(this.data);

            this.grid = new SlickGrid(`#${this.gridId}`, this.dataView, this.columns, this.options);

            this.grid.setSelectionModel(new SlickRowSelectionModel({ selectActiveRow: true }));

            this.grid.registerPlugin(new SlickAutoTooltips({ enableForHeaderCells: true }));
            if (groupItemMetadataProvider) {
                this.grid.registerPlugin(groupItemMetadataProvider);
            }

            // wire up model events to drive the grid
            this.dataView.onRowCountChanged.subscribe(() => {
                this.grid.updateRowCount();
                this.grid.render();
            });

            this.dataView.onRowsChanged.subscribe((_e, data) => {
                if (this.grid) {
                    this.grid.invalidateRows(data.rows);
                    this.grid.render();
                    if (this.selectedItem) {
                        this.scrollSelectedPartToTop(this.selectedPart, this.dataView, this.grid);
                    }
                }
            });

            // Selection handling
            this.grid.onSelectedRowsChanged.subscribe(() => {
                const selectedRowIndices = this.grid.getSelectedRows(); // Get the indices of selected rows
                if (selectedRowIndices && selectedRowIndices.length > 0) {
                    const selectedData = selectedRowIndices.map((index) => {
                        return this.dataView.getItem(index); // Assuming you're using dataView
                    });
                    this.selectedPart = selectedData[0].part;
                }
            });

            // set the dataView metadata defined in driver-select
            const getItemMetadata = function (i) {
                const item = selfRef.dataView.rows[i];
                if (item === undefined) {
                    return null;
                }
                // overrides for grouping rows
                if (item.__group) {
                    return selfRef.dataView._options.groupItemMetadataProvider.getGroupRowMetadata(item);
                }
                // overrides for totals rows
                if (item.__groupTotals) {
                    return selfRef.dataView._options.groupItemMetadataProvider.getTotalsRowMetadata(item);
                }
                return null;
            };
            this.dataView.getItemMetadata = this.dataViewMetadata(getItemMetadata);

            // Filtering
            $(this.grid.getHeaderRow()).delegate(':input', 'change keyup', function () {
                let columnId = $(this).data('columnId');
                if (columnId != null) {
                    selfRef.columnFilters[columnId] = $.trim($(this).val());
                    selfRef.dataView.refresh();
                }
            });

            this.grid.onHeaderCellRendered.subscribe((_e, data) => {
                $(data.node).empty();
                $(selfRef.generateTitleFromColumn(data.column)).appendTo(data.node);
            });

            // Populate the header row with the filters.
            this.grid.onHeaderRowCellRendered.subscribe(function (e, args) {
                $(args.node).empty();
                let filterQaAttrValue = args.column.qaAttrValue || args.column.id;

                if (args.column.options) {
                    let qaAttr = `${Constants.QA_ATTR_NAME}="${Constants.QA_ATTR_PREFIX}_${selfRef.qaAttrValue}_dlg_${filterQaAttrValue}_dd"`;
                    let inputString = `<select id="${args.column.id}_filteroption" class="slickgrid-filter-selection" ${qaAttr}><option value="">All</option>`;
                    args.column.options.forEach((option) => {
                        inputString += `<option value="${option}">${option}</option>`;
                    });
                    inputString += `</select>`;

                    $(inputString)
                        .data('columnId', args.column.id)
                        .val(selfRef.columnFilters[args.column.id])
                        .appendTo(args.node);
                } else {
                    switch (args.column.dataType) {
                        case 'numeric':
                        case 'pretty-numeric': {
                            $(
                                '<select id="' +
                                    args.column.id +
                                    '_filteroption" class="slickgrid-filter-relation">' +
                                    '<option value="greaterthan">&gt;</option>' +
                                    '<option value="greaterthanequal">&gt;=</option>' +
                                    '<option value="equal">=</option>' +
                                    '<option value="lessthan">&lt;</option>' +
                                    '<option value="lessthanequal">&lt;=</option>' +
                                    '</select>',
                            )
                                .data('columnId', args.column.id)
                                .val(selfRef.columnFilters[args.column.id])
                                .appendTo(args.node);

                            let qaAttr = `${Constants.QA_ATTR_NAME}="${Constants.QA_ATTR_PREFIX}_${selfRef.qaAttrValue}_dlg_${filterQaAttrValue}_txtbx"`;
                            $(
                                `<input id="${args.column.id}_filtertext" class="slickgrid-filter-relative-value" type="text" ${qaAttr} />`,
                            )
                                .data('columnId', args.column.id)
                                .val(selfRef.columnFilters[args.column.id])
                                .appendTo(args.node);

                            break;
                        }
                        case 'string': {
                            let qaAttr = `${Constants.QA_ATTR_NAME}="${Constants.QA_ATTR_PREFIX}_${selfRef.qaAttrValue}_dlg_${filterQaAttrValue}_txtbx"`;
                            $(
                                `<input id="${args.column.id}_filtertext" class="slickgrid-filter-text" type="text" ${qaAttr} />`,
                            )
                                .data('columnId', args.column.id)
                                .val(selfRef.columnFilters[args.column.id])
                                .appendTo(args.node);

                            break;
                        }
                        default: {
                            break;
                        }
                    }
                }
            });

            // Change the selected row on row click.
            this.grid.onClick.subscribe(function (e) {
                var cell = selfRef.grid.getCellFromEvent(e);

                if (e && e.target && e.target.className && e.target.className.includes('no-row-select')) return;
                else selfRef.grid.setSelectedRows([cell.row]);
            });

            // Hovering
            this.grid.onMouseEnter.subscribe((_e, args) => {
                const rowObj = this.getDesiredRowObject(_e, args.grid);
                if (rowObj) {
                    rowObj.classList.add('row-hover');
                    if (!rowObj.classList.contains('even')) {
                        rowObj.classList.remove('odd');
                    }
                }
            });
            this.grid.onMouseLeave.subscribe((_e, args) => {
                const rowObj = this.getDesiredRowObject(_e, args.grid);
                if (rowObj) {
                    rowObj.classList.remove('row-hover');
                    if (!rowObj.classList.contains('even')) {
                        rowObj.classList.add('odd');
                    }
                }
            });

            // Sorting
            this.grid.onSort.subscribe(function (e, args) {
                var columns = args.sortCols;
                if (columns.length > 0) {
                    selfRef.data.sort(function (dataRow1, dataRow2) {
                        let result = 0;
                        for (let i = 0; i < columns.length; i++) {
                            var field = columns[i].sortCol.field,
                                sign = columns[i].sortAsc ? 1 : -1,
                                value1 = dataRow1[field],
                                value2 = dataRow2[field];

                            switch (columns[i].sortCol.dataType) {
                                case 'numeric':
                                case 'pretty-numeric': {
                                    value1 = Number(PrettyNum.userEntryToNumber(value1));
                                    value2 = Number(PrettyNum.userEntryToNumber(value2));
                                    break;
                                }
                            }

                            result = (value1 == value2 ? 0 : value1 > value2 ? 1 : -1) * sign;
                            if (result !== 0) return result;
                        }
                        return result;
                    });
                } else {
                    selfRef.defaultSort();
                }

                selfRef.dataView.setItems(selfRef.data);
                selfRef.dataView.refresh();
            });

            // Turn on the lights!
            this.grid.init();

            // All of the code so far in the modifier has been happening _during_ rendering.
            // We're set up, but we don't want to perform a DataView update while we're still rendering, so punt it a micro-cycle.
            Promise.resolve().then(() => {
                this.dataView.beginUpdate();
                this.dataView.setItems(this.data);

                if (this.groupingConfig) {
                    this.dataView.setGrouping(this.groupingConfig);
                }

                this.dataView.setFilterArgs({
                    selfRef: this,
                    PrettyNum: PrettyNum,
                });
                // Grid filter function - gets passed into and created by SlickGrid.
                this.dataView.setFilter(function (item, filterArguments) {
                    var _selfRef = filterArguments.selfRef;
                    var _PrettyNum = filterArguments.PrettyNum;

                    for (var columnId in _selfRef.columnFilters) {
                        if (typeof columnId !== 'undefined' && _selfRef.columnFilters[columnId] !== '') {
                            var c = _selfRef.grid.getColumns()[_selfRef.grid.getColumnIndex(columnId)];
                            var itemVal = item[c.field];

                            var filterOptionId = '#' + columnId + '_filteroption';
                            var filterTextId = '#' + columnId + '_filtertext';
                            var foundMatch = false;

                            switch (c.dataType) {
                                case 'numeric':
                                case 'pretty-numeric': {
                                    const value = _PrettyNum.userEntryToNumber(
                                        document.querySelector(filterTextId).value,
                                    );
                                    if (
                                        !_selfRef.filterTypeTextColumn(_PrettyNum.userEntryToNumber(itemVal), {
                                            type: document.querySelector(filterOptionId).value,
                                            text: !isNaN(value) ? value : '',
                                        })
                                    )
                                        foundMatch = true;

                                    break;
                                }
                                case 'string':
                                    if (
                                        !item[c.field]
                                            .toLowerCase()
                                            .match(_selfRef.columnFilters[columnId].toLowerCase())
                                    )
                                        foundMatch = true;

                                    break;
                                default:
                                    if (
                                        !item[c.field]
                                            .toLowerCase()
                                            .match(_selfRef.columnFilters[columnId].toLowerCase())
                                    )
                                        foundMatch = true;

                                    break;
                            }

                            if (foundMatch) return 0;
                        }
                    }

                    return 1;
                });

                this.dataView.endUpdate();
                if (this.grid) {
                    this.dataView.syncGridSelection(this.grid, true);
                }

                // set data-qa-element on selected-row and first-row
                this.grid.onRendered.subscribe(() => {
                    // Get the container element where the rows are rendered
                    const container = this.grid.getContainerNode();
                    // Find all row elements within the container
                    const rowElements = container.querySelectorAll('.slick-row');
                    // Loop through each row element
                    rowElements.forEach((rowElement) => {
                        // Check if the row has the specified classes before adding the attribute
                        if (rowElement.classList.contains('selected-row')) {
                            rowElement.setAttribute(
                                'data-qa-element',
                                `${Constants.QA_ATTR_PREFIX}_${this.qaAttrValue}_dlg_sel_row`,
                            );
                        }

                        if (rowElement.classList.contains('first-row')) {
                            rowElement.setAttribute(
                                'data-qa-element',
                                `${Constants.QA_ATTR_PREFIX}_${this.qaAttrValue}_dlg_1st_row`,
                            );
                        }
                    });
                });

                window.addEventListener('resize', this.screenResized);
                // Delay needed on resize so that Firefox gets benefit - the need for it is a hack, anyway
                setTimeout(() => {
                    this.screenResized();
                }, 0);
            });
        }
    }

    dataViewMetadata(old_metadata_provider) {
        let selfRef = this;

        return function (row) {
            let item = selfRef.dataView.getItem(row);
            let ret = old_metadata_provider(row) || {};

            ret.cssClasses = ret.cssClasses || '';
            if (row === 1) ret.cssClasses += ' first-row';

            if (item) {
                if ('disabled' in item && item.disabled) {
                    ret.cssClasses += ' disabled-row';
                    ret.selectable = false;
                }

                if (item['selected']) ret.cssClasses += ' selected-row';

                // Set slew validation.
                if (typeof item.slewRateVus !== 'undefined') {
                    if (typeof this.errorCells === 'undefined') {
                        this.errorCells = { ...this.errorCells, [row]: {} };
                    }

                    if (item.slewRateVus < selfRef.requiredSlew) {
                        this.errorCells = { ...this.errorCells, [row]: { slewRateVus: 'error-cell' } };
                    } else {
                        this.errorCells = { ...this.errorCells, [row]: { slewRateVus: '' } };
                    }
                }

                selfRef.grid.setCellCssStyles('errorCells', this.errorCells);
            }

            if (ret.cssClasses) ret.cssClasses = ret.cssClasses.trim();

            return ret;
        };
    }

    generateTitleFromColumn(column) {
        let numberedMultiColumnSortElem = '';
        const cssRoot = document.querySelector(':root');
        if (column.sortable) {
            const options = this.grid?.getOptions();
            numberedMultiColumnSortElem =
                options && options.numberedMultiColumnSort ? '<span class="slick-sort-indicator-numbered"></span>' : '';
        }
        let fieldContextRow = '';
        if (typeof column.fieldContext !== 'undefined') {
            fieldContextRow = `<span class="slick-column-context">${column.fieldContext}</span>`;
            if (this.headerColumnRows < 2) {
                this.headerColumnRows = 2;
            }
        }
        if (typeof column.unit != 'undefined' && column.unit !== null) {
            this.headerColumnRows =
                this.headerColumnRows === 3 ||
                (this.headerColumnRows === 2 && typeof column.fieldContext !== 'undefined')
                    ? 3
                    : 2;
            const unitText = column.unit && column.unit != 'n/a' ? column.unit : '';
            const minMaxText = column.minMaxTyp ?? '';
            let contextText = '&nbsp;';
            if (unitText !== '' && minMaxText !== '') {
                contextText = `${minMaxText} | ${unitText}`;
            } else if (unitText !== '') {
                contextText = unitText;
            }
            cssRoot.style.setProperty('--alpine-header-name-row-count', this.headerColumnRows.toString());
            return (
                // Avoid newlines in the generated HTML
                '<div class="slick-header-column-group"><div class="slick-column-first-row">' +
                `<span class="slick-column-name">${column.name}</span>` +
                `<span class="slick-sort-indicator multi-row"></span>${numberedMultiColumnSortElem}</div>` +
                fieldContextRow +
                `<span class="slick-column-unit">${contextText}</span></div>`
            );
        } else {
            cssRoot.style.setProperty('--alpine-header-name-row-count', this.headerColumnRows.toString());
            if (column.sortable) {
                return (
                    `<div class="slick-header-column-group"><div class="slick-column-first-row">` +
                    `<span class="slick-column-name">${column.name}</span><span class="slick-sort-indicator"></span>${numberedMultiColumnSortElem}</div>${fieldContextRow}</div>
            `
                );
            } else {
                return `<div class="slick-header-column-group"><span class="slick-column-name">${column.name}</span>${fieldContextRow}</div>
            `;
            }
        }
    }

    getDesiredRowObject(e, gridObj) {
        const cell = gridObj.getCellFromEvent(e);
        let result;
        if (cell) {
            const frozenColumn = gridObj.getOptions().frozenColumn;
            if (cell.cell > frozenColumn) {
                result = gridObj.getCellNode(cell.row, 0)?.parentElement;
            } else {
                //left table row is hovered, gets right table row object
                const columnIndex = this.findVisibleColumn(e, gridObj);
                result = gridObj.getCellNode(cell.row, columnIndex)?.parentElement;
            }
        }
        return result || null;
    }

    findVisibleColumn(e, gridObj) {
        const cell = gridObj.getCellFromEvent(e);
        const columns = gridObj.getColumns();
        const frozenColumn = gridObj.getOptions().frozenColumn;
        let index = 0; //set to zero at default, for the case where there is no column in the right side of the table.
        if (cell) {
            for (let i = frozenColumn; i < columns.length; i++) {
                if (gridObj.getCellNode(cell.row, i + 1)) {
                    index = i + 1;
                    break;
                }
            }
        }
        return index;
    }

    filterTypeTextColumn(itemVal, filterObj) {
        if (!Utils.isNullOrWhiteSpace(filterObj.text)) {
            switch (filterObj.type) {
                case 'greaterthan':
                    if (
                        !PrettyNum.greaterThan(PrettyNum.eng3(Number(itemVal)), PrettyNum.eng3(Number(filterObj.text)))
                    ) {
                        return false;
                    }
                    break;
                case 'greaterthanequal':
                    if (
                        !PrettyNum.greaterThanEqualTo(
                            PrettyNum.eng3(Number(itemVal)),
                            PrettyNum.eng3(Number(filterObj.text)),
                        )
                    ) {
                        return false;
                    }
                    break;
                case 'equal':
                    if (!PrettyNum.equalTo(PrettyNum.eng3(Number(itemVal)), PrettyNum.eng3(Number(filterObj.text)))) {
                        return false;
                    }
                    break;
                case 'lessthan':
                    if (!PrettyNum.lessThan(PrettyNum.eng3(Number(itemVal)), PrettyNum.eng3(Number(filterObj.text)))) {
                        return false;
                    }
                    break;
                case 'lessthanequal':
                    if (
                        !PrettyNum.lessThanEqualTo(
                            PrettyNum.eng3(Number(itemVal)),
                            PrettyNum.eng3(Number(filterObj.text)),
                        )
                    ) {
                        return false;
                    }
                    break;
            }
        }
        return true;
    }

    scrollSelectedPartToTop(selectedPart, dataView, grid) {
        if (selectedPart) {
            let selectedRow = -1;
            const columns = grid.getColumns();
            const nameColumn = columns[1];
            if (columns.length > 1 && typeof nameColumn['field'] === 'string') {
                const fieldval = nameColumn.field;
                for (let i = 0; i < dataView.getLength(); i++) {
                    if (dataView.getItem(i)[fieldval] === selectedPart) {
                        selectedRow = i;
                    }
                }
            }
            if (selectedRow !== -1) {
                grid.setSelectedRows([selectedRow]);
                setTimeout(() => {
                    grid.scrollRowToTop(selectedRow - 1.75);
                }, 0);
            }
        }
    }

    _resetFilters() {
        // Clear the column filters.
        this.columnFilters = {};

        // Reset columns widths.
        for (let i = 0, iLen = this.columns.length; i < iLen; i++) {
            if (this.columns[i].originalWidth) this.columns[i].width = this.columns[i].originalWidth;
        }

        this.grid.setColumns(this.columns);

        // Reset the scroll position.
        var $viewport = $('#' + this.gridId + ' .slick-viewport');

        $viewport.scrollLeft(0);
        $viewport.scrollTop(0);

        // Find selected item
        var selectedItem = this.selectedItem;

        for (let i = 0; i < this.data.length; i++)
            this.data[i].selected = this.data[i].id == selectedItem.id ? true : false;

        for (let i = 0; i < this.columns.length; i++) {
            // Clear filter text box and reset the selected option to default.
            if (this.columns[i].dataType === 'numeric' || this.columns[i].dataType === 'pretty-numeric') {
                $('#' + this.columns[i].id + '_filtertext').val('');
                $('#' + this.columns[i].id + '_filteroption').val(this.columns[i].defaultFilterOption);
            }
        }

        this.defaultSort();
        this.grid.setSortColumns([]);
        this.dataView.setItems(this.data);
        this.dataView.refresh();
        this.grid.setSelectedRows(this.getSelectedRowsIndexes());
    }

    defaultSort() {
        this.data.sort(function (a, b) {
            return a.disabled === b.disabled ? Utils.compare(a.part, b.part) : a.disabled ? 1 : -1;
        });
    }

    getSelectedRowsIndexes() {
        // Find selected item
        if (this.selectedPart) {
            let selectedRow = -1;
            const columns = this.grid.getColumns();
            const nameColumn = columns[1];
            if (columns.length > 1 && typeof nameColumn['field'] === 'string') {
                const fieldval = nameColumn.field;
                for (let i = 0; i < this.dataView.getLength(); i++) {
                    if (this.dataView.getItem(i)[fieldval] === this.selectedPart) {
                        selectedRow = i;
                    }
                }
            }

            return [selectedRow];
        }

        return [];
    }
}
